import { useField } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { DatePickerField } from '../../../UIPackage/MainModule/components/DatePickerField';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import styles from './CommonGroupCondition.module.scss';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import { PassportGroupPassportTypeEnum } from '../enums/PassportGroupPassportTypeEnum';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import {
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogKnoAdminLevelsSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
} from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { catalogGet, catalogKnmTypes248Get } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import {
  getDictionariesAllKnmKindsSelectOptions,
  getDictionariesKnoOrganizationsSelectOptions,
  getDictionaryDecisionKindAllSelectOptions,
  getDictionaryReasonTypeAllSelectOptions,
  getDictionarySupervisionTypeAllSelectOptions,
  makeGetDictionariesProfKindsSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
} from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import {
  dictionariesItemValuesGet,
  dictionariesKnmKindsItemGet,
  dictionariesKnoOrganizationsItemGet,
  dictionariesProfKindsItemGet,
  dictionariesSupervisionTypesItemGet,
} from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { DictionaryNamesEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryNamesEnum';
import { IGroupCommonConditionsForm } from '../interfaces/IGroupCommonConditionsForm';
import { unableToRefuseSelectOptions } from '../constants/unableToRefuseSelectOptions';
import { passportGroupsGetRiskIndicatorsSelectOptions } from '../../MainModule/actions/passportGroupActions';
import { planListGetPlanSelectOptions } from '../../../PlanListPackage/MainModule/actions/planListActions';
import { knmListGetKnmSelectOptions } from '../../../KnmListPackage/MainModule/actions/knmListActions';
import { decisionListGetDecisionSelectOptions } from '../../../DecisionListPackage/MainModule/actions/decisionListActions';
import { preventionGetPmSelectOptions } from '../../../PreventionPackage/MainModule/actions/preventionActions';

interface ICommonGroupConditionProps {
  name: string;
  category: IGroupConditionForm['category'];
}

export const CommonGroupCondition = ({ name, category }: ICommonGroupConditionProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const controllingOrganizations: IGroupCommonConditionsForm['knoOrganizations'] = useField(
    `${name}.knoOrganizations`,
    {
      subscription: { value: true },
    },
  ).input.value;

  const isErknm = category === PassportGroupPassportTypeEnum.ERKNM;
  const isPm = category === PassportGroupPassportTypeEnum.PM;
  const isDecision = category === PassportGroupPassportTypeEnum.DECISION_PASSPORT;
  const isErp = category === PassportGroupPassportTypeEnum.ERP;

  const supervisionTypesConstraintUuids = useMemo(() => {
    return controllingOrganizations?.length ? controllingOrganizations.map(item => item.value.dictVersionId) : [];
  }, [controllingOrganizations]);

  const territorialUnitsSelectOptions = useSelector(getCatalogsTerritorialUnitsSelectOptions);
  const erknmOrganisationsSelectOptions = useSelector(getDictionariesKnoOrganizationsSelectOptions);
  const supervisionTypeSelectOptions = useSelector((state: IRootState) =>
    makeGetDictionariesSupervisionTypesExtendedSelectOptions()(state, supervisionTypesConstraintUuids, false, false),
  );
  const supervisionTypeAllSelectOptions = useSelector(getDictionarySupervisionTypeAllSelectOptions);
  const knoOrganizationAdminLevelsSelectOptions = useSelector(getCatalogKnoAdminLevelsSelectOptions);
  const knmTypes248AllSelectOptions = useSelector((state: IRootState) =>
    getCatalogKnmTypes248SelectOptionsSelector(state)({}),
  );
  const knmKindAllSelectOptions = useSelector(getDictionariesAllKnmKindsSelectOptions);
  const decisionKindAllSelectOptions = useSelector(getDictionaryDecisionKindAllSelectOptions);
  const pmKindAllSelectOptions = useSelector((state: IRootState) =>
    makeGetDictionariesProfKindsSelectOptions()(state, undefined),
  );
  const reasonTypeAllSelectOptions = useSelector(getDictionaryReasonTypeAllSelectOptions).map(item => ({
    ...item,
    label: item.shortTitle || item.title || '',
  }));

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.TERRITORIAL_UNITS));
    dispatch(dictionariesKnoOrganizationsItemGet());
    dispatch(dictionariesItemValuesGet(DictionaryNamesEnum.SUPERVISION_TYPE));
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNO_ADMIN_LEVELS));
    dispatch(catalogKnmTypes248Get({}));
    dispatch(dictionariesKnmKindsItemGet());
    dispatch(dictionariesItemValuesGet(DictionaryNamesEnum.ERVK));
    dispatch(dictionariesItemValuesGet(DictionaryNamesEnum.DECISION_KIND));
    dispatch(dictionariesProfKindsItemGet());
  }, [dispatch]);

  useEffect(() => {
    if (supervisionTypesConstraintUuids.length) {
      dispatch(dictionariesSupervisionTypesItemGet(supervisionTypesConstraintUuids, false));
    }
  }, [dispatch, supervisionTypesConstraintUuids]);

  const erpIdLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      switch (category) {
        case PassportGroupPassportTypeEnum.DECISION_PASSPORT:
          dispatch(decisionListGetDecisionSelectOptions(inputValue, callback));
          break;
        case PassportGroupPassportTypeEnum.ERKNM:
          dispatch(knmListGetKnmSelectOptions(inputValue, false, callback));
          break;
        case PassportGroupPassportTypeEnum.PM:
          dispatch(preventionGetPmSelectOptions(inputValue, callback));
          break;
        case PassportGroupPassportTypeEnum.ERP:
          dispatch(knmListGetKnmSelectOptions(inputValue, true, callback));
          break;
        default:
          break;
      }
    }, 500),
    [dispatch, category],
  );

  const planIdLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      switch (category) {
        case PassportGroupPassportTypeEnum.ERKNM:
          dispatch(planListGetPlanSelectOptions(inputValue, false, callback));
          break;
        case PassportGroupPassportTypeEnum.ERP:
          dispatch(planListGetPlanSelectOptions(inputValue, true, callback));
          break;
        default:
          break;
      }
    }, 500),
    [dispatch, category],
  );

  const riskIndikatorsLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      dispatch(passportGroupsGetRiskIndicatorsSelectOptions(inputValue, callback));
    }, 1000),
    [dispatch],
  );

  return (
    <KnmCollapse title="Общие сведения мероприятий" opened>
      <>
        <Row title={isDecision ? 'Период дат вынесения решения' : 'Период проведения'} justify="start" align="baseline">
          <DatePickerField name={`${name}.startDate`} />
          <span className={styles.DateFieldsSpace}>—</span>
          <DatePickerField name={`${name}.endDate`} />
        </Row>
        <FieldBlock label="Территориальная единица" margin="bottom">
          <SelectInputField
            name={`${name}.domains`}
            options={territorialUnitsSelectOptions}
            isClearable
            isSearchable
            isMulti
          />
        </FieldBlock>
        <Row>
          <CheckboxField
            id="includeDomainChild"
            name={`${name}.includeDomainChild`}
            label="Включая подчиненные организации"
            theme="system"
            type="checkbox"
          />
        </Row>
        <FieldBlock label="Контрольный (надзорный) орган" margin="bottom">
          <SelectInputField
            name={`${name}.knoOrganizations`}
            options={erknmOrganisationsSelectOptions}
            isClearable
            isSearchable
            isMulti
          />
        </FieldBlock>
        <Row>
          <CheckboxField
            id="includeChildKno"
            name={`${name}.includeKnoOrganizationChild`}
            label="Включая подчиненные организации"
            theme="system"
            type="checkbox"
          />
        </Row>
        <FieldBlock label="Уровень контрольного (надзорного) органа" margin="bottom">
          <SelectInputField
            name={`${name}.knoOrganizationLevels`}
            options={knoOrganizationAdminLevelsSelectOptions}
            isClearable
            isMulti
          />
        </FieldBlock>
        <FieldBlock label="Вид контроля (надзора)" margin="bottom">
          <SelectInputField
            name={`${name}.kindControls`}
            options={controllingOrganizations?.length ? supervisionTypeSelectOptions : supervisionTypeAllSelectOptions}
            isClearable
            isSearchable
            isMulti
          />
        </FieldBlock>
        {isErknm && (
          <FieldBlock label="Характер мероприятия" margin="bottom">
            <SelectInputField
              name={`${name}.knmTypes`}
              options={knmTypes248AllSelectOptions}
              isClearable
              isSearchable
              isMulti
            />
          </FieldBlock>
        )}
        {(isErknm || isErp) && (
          <FieldBlock label="Номер плана" margin="bottom">
            <SelectInputField
              name={`${name}.plans`}
              isClearable
              isAsync
              isSearchable
              loadOptions={planIdLoadOptions}
              isMulti
            />
          </FieldBlock>
        )}
        {isErknm && (
          <FieldBlock label="Вид мероприятия" margin="bottom">
            <SelectInputField
              name={`${name}.knmKinds`}
              options={knmKindAllSelectOptions}
              isClearable
              isSearchable
              isMulti
            />
          </FieldBlock>
        )}
        {isPm && (
          <FieldBlock label="Вид мероприятия" margin="bottom">
            <SelectInputField
              name={`${name}.pmKinds`}
              options={pmKindAllSelectOptions}
              isClearable
              isSearchable
              isMulti
            />
          </FieldBlock>
        )}
        {isDecision && (
          <FieldBlock label="Вид мероприятия" margin="bottom">
            <SelectInputField
              name={`${name}.decisionKinds`}
              options={decisionKindAllSelectOptions}
              isClearable
              isSearchable
              isMulti
            />
          </FieldBlock>
        )}
        {
          <>
            <FieldBlock label="Основание проведения мероприятия" margin="bottom">
              <SelectInputField
                name={`${name}.reasons`}
                options={reasonTypeAllSelectOptions}
                isClearable
                isSearchable
                isMulti
              />
            </FieldBlock>
            <FieldBlock label="Индикаторы риска" margin="bottom">
              <SelectInputField
                name={`${name}.riskIndikators`}
                isClearable
                isAsync
                isSearchable
                loadOptions={riskIndikatorsLoadOptions}
                isMulti
                cacheOptions={false}
              />
            </FieldBlock>
          </>
        }
        <FieldBlock label={isErp ? 'Учётный номер проверки' : 'Учётный номер мероприятия'} margin="bottom">
          <SelectInputField
            name={`${name}.erpIds`}
            isClearable
            isAsync
            isSearchable
            loadOptions={erpIdLoadOptions}
            isMulti
            cacheOptions={false}
          />
        </FieldBlock>
        {isPm && (
          <FieldBlock label="Профилактический визит, от которого нельзя отказаться" margin="bottom">
            <SelectInputField name={`${name}.unableToRefuse`} options={unableToRefuseSelectOptions} isClearable />
          </FieldBlock>
        )}
      </>
    </KnmCollapse>
  );
};
